import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { Printer } from 'react-feather';
import { Spinner } from 'admin-components/components';
import hash from 'object-hash';
import { useApolloClient, useMutation, useLazyQuery } from '@apollo/client';
import moment from 'moment';
import printReceiptMutation from 'components/mutations/useCustomerPresentMutation/mutations/printReceiptMutation';
import maskUpToLast4Digits from 'components/mutations/useCustomerPresentMutation/utils/maskUpToLast4Digits';
import { paymentsShape, customerShape } from 'constants/propTypes';
import Modal from 'components/ui/Modal';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { Cell, TableRow, Table, TableHeader } from 'components/ui/StyledTable';
import { FormattedMessage } from 'react-intl';
import { status, paymentMethodTranslation, codes, statusShortCode, REFUND_ACTION, RECEIPT_APPROVAL_CODES } from 'constants/payment';
import { CNP_PAYMENT_STATUS_FAILED, CNP_PAYMENT_STATUS_SUCCESS } from 'constants/customerNotPresent';
import { GET_VOUCHER_ALIAS, GET_PAYMENT_LINK } from 'store/queries';
import { CancelButton, ConfirmButton } from 'components/ui/Button';
import PaymentReference from './PaymentReference';
import CardReference from '../CardReference';
import PaymentLinkDetails from '../PaymentLinkDetails';

import {
  PaymentAction,
  PrintReceiptContainer,
  PrintLinkButton,
  ErrorMessage,
  CancelPaymentLinkModalButtonWrapper
} from './PaymentsSummary.styles';
import { isSocialMediaPaymentPending } from 'utils/booking';
import { CANCEL_PAYMENT_LINK } from 'store/mutations';

const getActionLabel = ({ action, method }) => {
  if (action === 'Payment' && method === 'Refund') {
    return method;
  }
  return action;
};

const PaymentsSummary = ({ payments, customer, bookingReference, isStationUser, className }) => {
  const [printingReceiptId, setPrintingReceiptId] = useState(null);
  const { push } = useHistory();
  const client = useApolloClient();
  const [printReceipt] = useMutation(printReceiptMutation);

  const [isPaymentLinkPopupShown, setPaymentLinkPopupShown] = useState(false);
  const [getPaymentLink, { data: paymentLinkData, loading: paymentLinkLoading, error: paymentLinkError, refetch: getPaymentLinkRefetch }] =
    useLazyQuery(GET_PAYMENT_LINK);

  const [isCancelPaymentLinkModalShown, setCancelPaymentLinkModalShown] = useState(false);
  const [isCancelPaymentLinkLoading, setCancelPaymentLinkLoading] = useState(false);
  const [isCancelPaymentLinkError, setCancelPaymentLinkError] = useState(false);

  const onCancelPaymentLinkClick = () => {
    setCancelPaymentLinkLoading(false);
    setCancelPaymentLinkError(null);
    setCancelPaymentLinkModalShown(true);
  };

  const onPaymentLinkClick = (paymentLinkId) => {
    getPaymentLink({
      variables: {
        linkId: paymentLinkId
      }
    });

    setPaymentLinkPopupShown(true);
  };

  const onCancelPaymentLinkCancel = () => {
    setCancelPaymentLinkModalShown(false);
  };

  const onCancelPaymentLinkConfirm = async () => {
    const payment = payments.find(
      (p) =>
        p.paymentStatus === statusShortCode.pending &&
        p.method === codes.SOCIAL_MEDIA &&
        p.externalReference === paymentLinkData.getPaymentLink.id
    );

    setCancelPaymentLinkLoading(true);

    try {
      const resp = await client.mutate({
        mutation: CANCEL_PAYMENT_LINK,
        variables: {
          bookingReference,
          paymentRefId: payment.refId
        }
      });

      payments = resp.data.cancelPaymentLink.payments;

      getPaymentLinkRefetch({
        variables: {
          linkId: payment.externalReference
        }
      });

      setCancelPaymentLinkModalShown(false);
    } catch (error) {
      setCancelPaymentLinkError(error);
    }

    setCancelPaymentLinkLoading(false);

    setCancelPaymentLinkModalShown(false);
  };

  const onVoucherPaymentReferenceClick = async (reference) => {
    const { data: { getVoucherAliasByLastCharsAndBookingReference: alias } = {} } =
      (await client.query({
        query: GET_VOUCHER_ALIAS,
        variables: {
          voucherCodeLastChars: reference,
          bookingReference
        }
      })) || {};
    if (alias) {
      push(`/vouchers/${alias}`);
    }
  };

  const onVoucherAliasClick = async (alias) => {
    push(`/vouchers/${alias}`);
  };

  const isEmptyVoucher = (p) => p.method === codes.VOUCHER && p.amount === 0;

  const onPrintReceiptClick = async (payment) => {
    const printReceiptData = {
      variables: {
        receiptInput: payment.receiptData
          ? {
              aId: payment.receiptData.aid,
              amount: parseFloat(payment.receiptData.totalAmount.replace(/[^\d.]/g, '')).toFixed(2),
              authMethod: payment.receiptData.authMethod,
              paymentStatus: payment.receiptData.paymentStatus,
              bookingReference,
              cardType: payment.receiptData.paymentMethod,
              paymentReference: payment.action === REFUND_ACTION ? payment.externalReference : payment.reference,
              currency: payment.currency,
              customerName: `${customer?.firstName} ${customer?.lastName}`,
              customerPan: payment.receiptData.cardNumber,
              issueNumber: payment.receiptData.panSeq,
              merchantId: maskUpToLast4Digits(payment.receiptData.mid),
              paymentDate: moment(payment.receiptData.date, 'DD/MM/YYYY').format('DD/MM/YY'),
              paymentTime: payment.receiptData.time,
              receiptNumber: payment.receiptData.stan,
              terminalId: maskUpToLast4Digits(payment.receiptData.terminalId),
              verification: payment.receiptData.authMethod,
              approvalCode: payment.receiptData.authCode,
              // display failed (cancelled) CP payment as refund in receipt
              action: payment.paymentStatus === statusShortCode.failed ? REFUND_ACTION : payment.action,
              APACS50TN: payment.receiptData.apacs50tn
            }
          : {
              aId: '',
              amount: payment.amount.toFixed(2),
              authMethod: '',
              paymentStatus: payment.paymentStatus === statusShortCode.success ? CNP_PAYMENT_STATUS_SUCCESS : CNP_PAYMENT_STATUS_FAILED,
              bookingReference,
              cardType: '',
              paymentReference: payment.action === REFUND_ACTION ? payment.externalReference : payment.reference,
              currency: payment.currency,
              customerName: `${customer?.firstName} ${customer?.lastName}`,
              customerPan: payment.action === REFUND_ACTION ? '' : `****${payment.externalReference}`,
              issueNumber: '',
              merchantId: '',
              paymentDate: moment(payment.transactionTimestamp).format('DD/MM/YY'),
              paymentTime: moment(payment.transactionTimestamp).format('HH:MM'),
              receiptNumber: '',
              terminalId: '',
              verification: '',
              approvalCode:
                payment.paymentStatus === statusShortCode.success ? RECEIPT_APPROVAL_CODES.APPROVED : RECEIPT_APPROVAL_CODES.DECLINED,
              action: payment.action,
              APACS50TN: ''
            }
      }
    };
    try {
      setPrintingReceiptId(payment.id);
      await printReceipt(printReceiptData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setPrintingReceiptId(null);
    }
  };

  return (
    <>
      <Table data-cy="payments-summary" className={className}>
        <TableHeader columns={15}>
          <Cell span={2}>
            <FormattedMessage id="paymentsSummary.date" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="paymentsSummary.action" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="paymentsSummary.amount" />
          </Cell>
          <Cell span={2}>
            <FormattedMessage id="paymentsSummary.method" />
          </Cell>
          <Cell span={3}>
            <FormattedMessage id="paymentsSummary.paymentReference" />
          </Cell>
          <Cell span={3}>
            <FormattedMessage id="paymentsSummary.cardReference" />
          </Cell>
          <Cell span={1}>
            <FormattedMessage id="paymentsSummary.status" />
          </Cell>
        </TableHeader>

        {payments.map((p) => {
          const actionLabel = getActionLabel(p);
          return (
            p.display &&
            !isEmptyVoucher(p) && (
              <TableRow columns={15} key={hash(p)} position="relative" data-testid="payment-content" data-cy="payment-content">
                <Cell alignItems="center" span={2} data-cy="cell">
                  {moment(p.transactionTimestamp).format('DD/MM/YY HH:mm')}
                </Cell>
                <Cell alignItems="center" span={2} data-cy="cell">
                  <PaymentAction action={actionLabel.toLowerCase()}>{actionLabel}</PaymentAction>
                </Cell>
                <Cell alignItems="center" span={2} data-cy="cell">
                  <FormattedCurrency amount={Math.abs(p.amount)} currency={p.currency} />
                </Cell>
                <Cell alignItems="center" span={2} data-cy="cell">
                  {paymentMethodTranslation[p.method] ? <FormattedMessage id={paymentMethodTranslation[p.method]} /> : p.method}
                </Cell>
                <Cell alignItems="center" span={3} data-cy="cell">
                  <PaymentReference
                    payment={p}
                    onVoucherReferenceClick={onVoucherPaymentReferenceClick}
                    onVoucherAliasClick={onVoucherAliasClick}
                  />
                </Cell>
                <Cell alignItems="center" span={3} data-cy="cell">
                  <CardReference payment={p} onPaymentLinkClick={onPaymentLinkClick} />
                </Cell>
                <Cell alignItems="center" span={1} data-cy="cell">
                  {status[p.paymentStatus] ? <FormattedMessage id={status[p.paymentStatus]} /> : p.paymentStatus}
                </Cell>
                <PrintReceiptContainer>
                  {isStationUser && p.method === codes.CARD && (
                    <PrintLinkButton onClick={() => onPrintReceiptClick(p)}>
                      <span className="print-message">
                        <FormattedMessage id="receipt.print" />
                      </span>
                      {printingReceiptId === p.id ? <Spinner /> : <Printer />}
                    </PrintLinkButton>
                  )}
                </PrintReceiptContainer>
              </TableRow>
            )
          );
        })}
      </Table>

      <Modal
        isOpen={isCancelPaymentLinkModalShown}
        onClose={() => setCancelPaymentLinkModalShown(false)}
        title={<FormattedMessage id="paymentLink.cancel.modal.question" />}
      >
        {isCancelPaymentLinkLoading && <Spinner />}
        {!isCancelPaymentLinkLoading && (
          <>
            <CancelPaymentLinkModalButtonWrapper>
              <ConfirmButton data-testid="cancel-payment-link-confirm-btn" onClick={onCancelPaymentLinkConfirm}>
                <FormattedMessage id="paymentLink.cancel.modal.confirm" />
              </ConfirmButton>

              <CancelButton onClick={onCancelPaymentLinkCancel}>
                <FormattedMessage id="paymentLink.cancel.modal.cancel" />
              </CancelButton>
            </CancelPaymentLinkModalButtonWrapper>

            {isCancelPaymentLinkError && (
              <ErrorMessage>
                <FormattedMessage id="paymentLink.notFoundError" />
              </ErrorMessage>
            )}
          </>
        )}
      </Modal>

      <Modal
        isOpen={isPaymentLinkPopupShown}
        onClose={() => setPaymentLinkPopupShown(false)}
        title={<FormattedMessage id="paymentLink.details" />}
      >
        {paymentLinkLoading && <Spinner />}
        {!paymentLinkLoading && (
          <>
            {paymentLinkError && (
              <ErrorMessage>
                <FormattedMessage id="paymentLink.notFoundError" />
              </ErrorMessage>
            )}
            {!paymentLinkError && paymentLinkData && <PaymentLinkDetails {...paymentLinkData.getPaymentLink} />}

            {!paymentLinkError && paymentLinkData && isSocialMediaPaymentPending({ payments }, paymentLinkData.getPaymentLink.id) && (
              <CancelButton data-cy="cancel-payment-link-btn" data-testid="cancel-payment-link-btn" onClick={onCancelPaymentLinkClick}>
                <FormattedMessage id="paymentLink.cancel" />
              </CancelButton>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

PaymentsSummary.propTypes = {
  payments: paymentsShape,
  customer: customerShape,
  bookingReference: PropTypes.string,
  className: PropTypes.string,
  isStationUser: PropTypes.bool
};

export default PaymentsSummary;
